import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import { ElMessage } from 'element-plus';
import type { ApiResponse } from '../types/models';
import { isUuid, newUuid } from '../utils/idempotency';

/**
 * 契约口径：《后端项目开发文档 v1.6》§6.0（前后端联调唯一口径）。
 * - ③ 业务串走 `resultCode`，成功固定 `"OK"`；**判成败唯一依据 `resultCode === "OK"`**（项目负责人 seq=2216
 *      换判法）。`code` 那枚字符串不再参与判断：后端 09-18 那趟只新增 `resultCode`，等四端都不读它才由
 *      `code` 翻成契约定义的整数派生值（seq=2215）⇒ 全链路既不比 `code == "OK"`，也不按 int 比较业务码。
 * - ④ HTTP 语义冻结：200 业务失败也走 200 + `resultCode!="OK"`；401 清态；403 静默拦截；409 冲突；410 链接过期；
 *      422 业务规则拒绝；423 账号锁定；429 退避重试；**503 从 5xx 单列**（依赖不可用，不作缺陷报障）。
 * - ⑥ 幂等键 `Idempotency-Key: <uuid>`，值必须是合法 UUID；三态分别处置（缺键/非法键属**前端缺陷**，不得提示用户）。
 * - ⑦ 乐观锁 `rowversion` 读回必带写，`ROWVERSION_CONFLICT` 禁止自动覆盖或静默重试。
 * - 提示文案一律取 `message`，只在需要特殊交互时按 `resultCode` 分支。
 * - ⚠ 以上只管**信封层**。批量条目里那枚 `items[].code`（以及建单校验 `checks[].code`）契约仍是字符串真码名，
 *      跟信封那枚同名不同义，按后端 seq=2210 的提醒 ⛔ 用本文件的通用解析去吃那一层。
 */
export const ApiCode = {
  /** §6.0-③ 成功业务码固定字符串 */
  OK: 'OK',
  /* —— 跨切基线（§6.0-④）—— */
  BAD_REQUEST: 'BAD_REQUEST',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  FORBIDDEN: 'FORBIDDEN',
  DATA_SCOPE_DENIED: 'DATA_SCOPE_DENIED',
  END_BINDING_CONFLICT: 'END_BINDING_CONFLICT',
  NOT_FOUND: 'NOT_FOUND',
  RATE_LIMITED: 'RATE_LIMITED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  DEPENDENCY_UNAVAILABLE: 'DEPENDENCY_UNAVAILABLE',
  STATE_GUARD_REJECTED: 'STATE_GUARD_REJECTED',
  /* —— 前端必须按 code 分支的专项码 —— */
  /** §6.0-⑦ 乐观锁：数据被他人变更 → 刷新后由用户决定重试 */
  ROWVERSION_CONFLICT: 'ROWVERSION_CONFLICT',
  /** §6.0-⑥ 幂等三态：前两者属前端封装缺陷，不得提示用户 */
  IDEMPOTENCY_KEY_MISSING: 'IDEMPOTENCY_KEY_MISSING',
  IDEMPOTENCY_KEY_INVALID: 'IDEMPOTENCY_KEY_INVALID',
  IDEMPOTENCY_KEY_REUSED: 'IDEMPOTENCY_KEY_REUSED',
  /** 登录失败走 HTTP 200，刻意不触发全局 401 —— 登录页须自行按 code 显示 */
  AUTH_LOGIN_FAILED: 'AUTH_LOGIN_FAILED',
  /** 423：5 次失败锁 15min —— 禁用登录按钮，继续重试只会延长锁定 */
  AUTH_ACCOUNT_LOCKED: 'AUTH_ACCOUNT_LOCKED',
  AUTH_SESSION_EXPIRED: 'AUTH_SESSION_EXPIRED',
  /** 410：导出链接过期 —— 不重试原链接，引导重走审批 */
  EXPORT_LINK_EXPIRED: 'EXPORT_LINK_EXPIRED',
  EXPORT_APPROVAL_REQUIRED: 'EXPORT_APPROVAL_REQUIRED',
  /**
   * S-10（seq372 §二）：这里**没有**批量条目级守卫码。后端 `ba9cfb7` 已删 `BATCH_ITEM_STATUS_GUARD`，
   * `items[].code` 从来就是该条守卫真实返回的具体码（`ORDER_NOT_FOUND` /
   * `ORDER_STATUS_ILLEGAL_TRANSITION` / `URGE_ORDER_NOT_APPLICABLE` …）。不为不存在的码留常量与分支。
   */
  ORDER_DELIVERY_METHOD_UNSUPPORTED_P0: 'ORDER_DELIVERY_METHOD_UNSUPPORTED_P0',
  /**
   * seq322 §七 D-06：`assign`/`reassign`/`pool/assign` 的 `courierId` **有值但库内无此交付员**
   * → 422 本码（业务不可执行，要提示用户换人）；**缺失/空/非正数** → 400 `VALIDATION_FAILED`
   * （请求体不合法，前端应在提交前拦截、不该发出请求）。两码不得合并处置。
   */
  COURIER_NOT_FOUND: 'COURIER_NOT_FOUND',
} as const;

/** HTTP 层语义（§6.0-④），与业务码分开，避免再次混用 int 当业务码 */
const Http = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  METHOD_NOT_ALLOWED: 405,
  CONFLICT: 409,
  /** §6.0-④ 新增：导出链接已过期 */
  GONE: 410,
  /** §6.0-④ 新增：账号已锁定 */
  LOCKED: 423,
  GUARD_REJECTED: 422,
  TOO_MANY: 429,
  /** §6.0-④：从 5xx 单列，提示「稍后重试」而非报障 */
  DEPENDENCY_UNAVAILABLE: 503,
} as const;

/**
 * 契约漂移取证：命中即记一条**样本证据**（不静默容忍），供 `getContractDrift()` 导出后回报后端。
 * v1.6 起后端把统一封装重构为字符串业务码（`code=="OK"`），本轮又按 seq=2183 补 `resultCode` 作业务码真源；
 * 原先为「万一打到旧部署包」留的 `int 0` / `isSuccess` 兼容分支已随换判法整枚撤掉（seq=2216）——
 * 留着就是继续读被派生的那一枚，后端翻整数那步就永远迈不出去。
 *
 * 导出给 API 适配层复用：端点已冻结但**字段名未冻结**（如看板族）时，适配一次并在此记账。
 */
const driftSamples = new Map<string, { count: number; sample: string }>();

export function recordDrift(kind: string, observed: unknown): void {
  const hit = driftSamples.get(kind);
  if (hit) {
    hit.count += 1;
    return;
  }
  driftSamples.set(kind, { count: 1, sample: JSON.stringify(observed) });
  console.warn(`[contract-drift] ${kind} 与 §6.0 冻结口径不一致，实测：${JSON.stringify(observed)}`);
}

export function getContractDrift(): Array<{ kind: string; count: number; sample: string }> {
  return Array.from(driftSamples.entries()).map(([kind, v]) => ({ kind, ...v }));
}

/**
 * 信封层的业务码名：**只认 `resultCode` 一枚**（项目负责人 seq=2216、seq=2215）。
 * 返回 `undefined` ＝ 这枚应答没带可判的业务串 ⇒ 既判不了成败也定不了因，
 * 与 `utils/readState.ts` 里 `codeSource: 'none'` 是同一件事，⛔ 拿被派生的 `code` 顶上。
 */
export function envelopeResultCode(body: Partial<ApiResponse<unknown>> | undefined): string | undefined {
  const value = body?.resultCode;
  if (typeof value !== 'string') return undefined;
  const text = value.trim();
  return text === '' ? undefined : text;
}

/**
 * §6.0-③ 判成败：唯一依据 `resultCode === "OK"`。
 *
 * 原先那三条兼容支（比 `code == "OK"`、非冻结大小写、int 0 与 `isSuccess` 布尔）按 seq=2216 整枚撤掉：
 * 只要本端还在读 `code`，后端那步「等真没人读它了再翻整数」（seq=2215）就永远迈不出去。
 * 缺 `resultCode` 一律判失败并在漂移里留证据 —— 宁可在读态上如实报「服务端没给可判的码」，
 * ⛔ 拿 HTTP 200 当成功（登录那枝就是 200 + 失败码，seq=2183 已定「四端判成败只看 resultCode」）。
 */
export function isEnvelopeSuccess(body: Partial<ApiResponse<unknown>> | undefined): boolean {
  const code = envelopeResultCode(body);
  if (code === undefined) {
    recordDrift('信封未带 resultCode（本端按 seq=2216 只认这枚键，缺它即判不了成败）', {
      code: body?.code,
      hasResultCode: body ? 'resultCode' in body : false,
    });
    return false;
  }
  return code === ApiCode.OK;
}

export class ApiError extends Error {
  /**
   * 报障用的**派生显示值**：信封的 `code`（后端换判法后会翻成整数）或 HTTP 状态。
   * ⛔ 参与判断——按业务码名分支一律读 `resultCode`（seq=2216）。
   */
  readonly code: string | number;
  /**
   * §6.0-③ 业务码唯一载体，也是本端**唯一**可据以分支的一枚。
   * 后端没带这枚键时就是 `undefined`（定不了因，走通用文案），⛔ 从 `code` 反推。
   */
  readonly resultCode?: string;
  readonly httpStatus?: number;
  readonly traceId?: string;
  readonly payload?: unknown;

  constructor(
    message: string,
    code: string | number,
    httpStatus?: number,
    traceId?: string,
    payload?: unknown,
    resultCode?: string,
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.httpStatus = httpStatus;
    this.traceId = traceId;
    this.payload = payload;
    this.resultCode = resultCode;
  }

  /** 从信封失败应答构造：成败已由 `isEnvelopeSuccess` 判过，这里只把定因材料带全。 */
  static fromEnvelope(
    body: Partial<ApiResponse<unknown>> | undefined,
    httpStatus: number | undefined,
    fallbackMessage: string,
  ): ApiError {
    return new ApiError(
      body?.message || fallbackMessage,
      body?.code ?? httpStatus ?? ApiCode.INTERNAL_ERROR,
      httpStatus,
      body?.traceId ?? undefined,
      body?.data,
      envelopeResultCode(body),
    );
  }

  private is(code: string): boolean {
    return this.resultCode === code;
  }

  /** §6.0-⑦ 乐观锁冲突：与幂等/抢单/已派单同处 409，但文案与处置不同，必须按 code 分支 */
  get isRowversionConflict(): boolean {
    return this.is(ApiCode.ROWVERSION_CONFLICT);
  }

  /** seq322 §七 D-06 的 422 分支：所选交付员库内不存在 → 提示换人，不是表单校验缺陷 */
  get isCourierNotFound(): boolean {
    return this.is(ApiCode.COURIER_NOT_FOUND);
  }

  /**
   * §6.0-⑥ + seq322 §五 裁定（v1.8 权威口径）：**同键 + 同 payload → 200 `OK` + 原结果**（含原
   * `orderNo`），是弱网重试/重复点击的透明重放，走成功分支、无需二次查询；
   * **同键 + 不同 payload → 409 `IDEMPOTENCY_KEY_REUSED`** 才是这里命中的分支 —— 键空间污染，
   * 必须显式拒绝。前端**绝不可**为此自动换新键重发：那正是绕过幂等、直接造出重复订单的路径。
   */
  get isIdempotencyReused(): boolean {
    return this.is(ApiCode.IDEMPOTENCY_KEY_REUSED);
  }

  /** §6.0-⑥ 缺键 / 键非 UUID：属**前端封装缺陷**，不得提示用户，只记证据 + console.error */
  get isIdempotencyFrontendDefect(): boolean {
    return this.is(ApiCode.IDEMPOTENCY_KEY_MISSING) || this.is(ApiCode.IDEMPOTENCY_KEY_INVALID);
  }

  /** 409 族（乐观锁 / 幂等 / 抢单竞争 / 已派单 / 配置版本冲突）：一律不自动覆盖 */
  get isConflict(): boolean {
    return this.httpStatus === Http.CONFLICT;
  }

  get isUnauthorized(): boolean {
    return this.httpStatus === Http.UNAUTHORIZED || this.is(ApiCode.UNAUTHENTICATED) || this.is(ApiCode.TOKEN_EXPIRED);
  }

  /** §6.0-④ 403：已鉴权无权限（数据范围越权、端绑定互斥、导出未审批、审计不可改）——静默拦截、不重试 */
  get isForbidden(): boolean {
    return (
      this.httpStatus === Http.FORBIDDEN ||
      this.is(ApiCode.FORBIDDEN) ||
      this.is(ApiCode.DATA_SCOPE_DENIED) ||
      this.is(ApiCode.END_BINDING_CONFLICT) ||
      this.is(ApiCode.EXPORT_APPROVAL_REQUIRED)
    );
  }

  /** §6.0-④ 422：状态机守卫 / 业务规则拒绝（十余个码共用一档，文案取 message） */
  get isGuardRejected(): boolean {
    return this.httpStatus === Http.GUARD_REJECTED;
  }

  /** 423 账号锁定：禁用登录按钮，继续重试只会延长锁定 */
  get isAccountLocked(): boolean {
    return this.httpStatus === Http.LOCKED || this.is(ApiCode.AUTH_ACCOUNT_LOCKED);
  }

  /** 410 导出链接过期：不重试原链接 */
  get isExportLinkExpired(): boolean {
    return this.httpStatus === Http.GONE || this.is(ApiCode.EXPORT_LINK_EXPIRED);
  }

  /** 503 依赖不可用：从 5xx 单列，不作缺陷报障 */
  get isDependencyUnavailable(): boolean {
    return this.httpStatus === Http.DEPENDENCY_UNAVAILABLE || this.is(ApiCode.DEPENDENCY_UNAVAILABLE);
  }

  /**
   * 端点未部署（§6.2 缺口）——区别于「资源不存在」，仅供统一错误处置判「不必打扰用户」。
   * ⚠ 缺口回落桩已整体下线（用户 m00124 裁定）：404/405 一律**直接向上抛错**，本端不再回任何桩数据，
   * 调用方要么自行处置、要么让错误冒泡，⛔ 不从这里换一条「看起来成功」的应答。
   */
  get isEndpointMissing(): boolean {
    return this.httpStatus === Http.NOT_FOUND || this.httpStatus === Http.METHOD_NOT_ALLOWED;
  }

  /** 面向用户的文案：冻结口径要求 5xx 携带 traceId 报障 */
  get displayMessage(): string {
    const base = this.message || '请求失败';
    const withTrace = (text: string): string => (this.traceId ? `${text}（traceId: ${this.traceId}）` : text);
    if (this.isRowversionConflict) return withTrace(`${base}｜数据已被他人变更，请刷新后重试`);
    if (this.isIdempotencyReused) return withTrace('该提交键已被另一笔内容占用，本次未被执行；请返回上一步核对内容后重新发起，不要更换新键重录（会产生重复单据）');
    if (this.isAccountLocked) return withTrace(`${base}｜锁定期间继续尝试只会延长时间`);
    if (this.isExportLinkExpired) return withTrace('下载链接已过期，请重新发起导出审批');
    if (this.isDependencyUnavailable) return withTrace('服务依赖暂不可用，请稍后重试');
    return withTrace(base);
  }
}

declare module 'axios' {
  export interface AxiosRequestConfig {
    /** 写操作幂等键，由请求头 Idempotency-Key 承载（§6.0-⑥） */
    idempotencyKey?: string;
    /** 跳过鉴权头（登录） */
    skipAuth?: boolean;
    /** 网络错误/5xx/429 自动重试次数（**仅只读方法生效**；写操作受 S-13 禁令一律不自动重试） */
    retry?: number;
    /** 静默模式：不弹全局错误提示，由调用方处理 */
    silent?: boolean;
  }
}

export interface TokenProvider {
  getAccessToken(): string | null;
  onUnauthorized(): void;
}

let tokenProvider: TokenProvider = {
  getAccessToken: () => null,
  onUnauthorized: () => undefined,
};

export function bindTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

const END = import.meta.env.VITE_APP_END || 'ADMIN';

const READONLY_METHODS = new Set(['get', 'head', 'options']);

/**
 * S-13 落地前的写操作重试禁令（seq340）：服务端对 `Idempotency-Key` 当前只做
 * 「存在性 + UUID 格式」校验，**既无登记表比对也无结果缓存** ⇒ 「带同一个键重放是安全的」
 * 这个前提为假，网络抖动重试 / 超时重发会真实改坏数据。所以只读方法保留退避重试，
 * 写操作一律不自动重试；网络抖动、超时、401 刷新后重放三条自动路径全部关闭。
 * 用户**手动**再点一次按 seq322 §五 C-05 第一行走「同键重放」语义（`withIdempotency` 按业务
 * 意图复用同一 UUID，不换键），但同键重放要拿回原结果仍取决于 S-13 的登记表，故视图层另加
 * 「一次业务意图只允许一条在途写」的单飞保护。
 */
function shouldRetry(config: InternalAxiosRequestConfig & { __retryCount?: number }): boolean {
  const method = (config.method || 'get').toLowerCase();
  if (!READONLY_METHODS.has(method)) {
    if (config.retry) {
      recordDrift('S-13 未落地前写操作不得自动重试（调用方显式请求的重试已就地禁用）', `${method.toUpperCase()} ${config.url ?? ''}`);
    }
    return false;
  }
  const max = config.retry ?? (method === 'get' ? 2 : 0);
  const done = config.__retryCount ?? 0;
  return done < max;
}

function backoff(attempt: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 220 * (attempt + 1)));
}

/**
 * 统一的用户侧错误处置策略（§6.0-④）。
 * HTTP 非 2xx 与「HTTP 200 + `resultCode !== "OK"`」两条路必须共用同一个入口：axios 的 fulfilled
 * 分支内 throw 不会被同一拦截器的 rejected 分支捕获，若只在 rejected 分支提示，
 * 状态守卫、导出链接过期、依赖不可用等 200 业务失败会全部静默——用户看不到任何反馈。
 */
function surfaceError(apiError: ApiError, config?: InternalAxiosRequestConfig): void {
  if (apiError.isUnauthorized) {
    tokenProvider.onUnauthorized();
  }

  if (apiError.isIdempotencyFrontendDefect) {
    // §6.0-⑥：缺键 / 键非法属前端自身缺陷，**不得提示用户**；保留开发侧证据便于定位调用点
    console.error(
      `[idempotency-defect] ${apiError.code} ${config?.method?.toUpperCase() ?? ''} ${config?.url ?? ''} traceId=${apiError.traceId ?? '-'}`,
    );
    return;
  }

  // silent 由调用方自渲染（如登录页需按码给文案）；403 静默拦截不重试；
  // 404/405 是「端点未部署」——桩已整体下线，错误照常向上抛，这里只不弹全局红条
  if (config?.silent || apiError.isForbidden || apiError.isEndpointMissing) return;

  // §七 D-06 的 422 分支：契约要求「告知用户换人」，通用文案无法承载这个动作，
  // 故全局静默，由分配弹窗就地清空人选并给出可操作提示（与 400 的行内红框分路）。
  if (apiError.isCourierNotFound) return;

  ElMessage({
    type: apiError.isConflict || apiError.isGuardRejected ? 'warning' : 'error',
    message: apiError.displayMessage,
  });
}

export interface HttpClientOptions {
  adapter?: AxiosRequestConfig['adapter'];
  /** §6.0-①：base 即后端根（`http://<backend-host>:<port>` 或同源 ''），**不得拼 /api** */
  baseURL?: string;
}

/**
 * §6.0-③ 序列化约定实测补充：后端主键为 `long`，线上是 JSON **数字**，而前端模型统一以
 * **字符串**承载 id（见 `utils/contract.ts`）。不在出口归一，就会出现
 * 「路由参数 `'12'` === 响应里的 `12`」恒假的静默失配（详情跳转会白屏一类的根因）。
 * 只处理 `id` / `*Id` / `*Ids` 三种命名，其它数值（fee、counts、permVersion…）原样保留。
 */
function isIdKey(key: string): boolean {
  return key === 'id' || key.endsWith('Id') || key.endsWith('Ids');
}

export function stringifyWireIds<T>(value: T, depth = 0): T {
  if (depth > 12 || value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map((item) => stringifyWireIds(item, depth + 1)) as unknown as T;
  const out: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!isIdKey(key)) {
      out[key] = stringifyWireIds(raw, depth + 1);
      continue;
    }
    // 超出安全整数的 long 在 JSON.parse 阶段已丢精度，这里只做类型归一，不假装能恢复
    out[key] = Array.isArray(raw)
      ? raw.map((item) => (typeof item === 'number' ? String(item) : stringifyWireIds(item, depth + 1)))
      : typeof raw === 'number'
        ? String(raw)
        : stringifyWireIds(raw, depth + 1);
  }
  return out as unknown as T;
}

export function createHttpClient(options: HttpClientOptions = {}): AxiosInstance {
  const baseURL = options.baseURL ?? import.meta.env.VITE_API_BASE_URL ?? '';

  const client = axios.create({
    baseURL,
    timeout: 20000,
    headers: { 'Content-Type': 'application/json' },
    adapter: options.adapter,
    // COORD-RULINGS §I 终态：鉴权只走 `Authorization: Bearer`、不依赖 Cookie，凭证模式与
    // 令牌头架构本就互斥，故显式写死 false（防后来者为联调顺手打开）。§I 已撤回
    // 「ACAO:* + credentials 会被浏览器拒」那条推断，本值不以任何 CORS 报错为依据——
    // **前端不为 CORS 做任何改动**，跨域归部署侧与 dev 代理负责。
    withCredentials: false,
  });

  client.interceptors.request.use((config) => {
    const token = tokenProvider.getAccessToken();
    if (token && !config.skipAuth) {
      config.headers.set('Authorization', `Bearer ${token}`);
    }
    config.headers.set('X-Client-End', END);

    // §6.0-⑥：写操作一律携带合法 UUID 幂等键。缺键 → 409 MISSING、非 UUID → 400 INVALID，
    // 两者都被契约判定为**前端封装缺陷**（不得提示用户），故在出口兜底生成/纠正；
    // 调用方传入的非法键不静默吞掉——记一条漂移证据，便于回查修调用点。
    const method = (config.method || 'get').toLowerCase();
    const isWrite = method !== 'get' && method !== 'head' && method !== 'options';
    if (isWrite) {
      const provided = config.idempotencyKey;
      if (provided && !isUuid(provided)) {
        recordDrift('调用方传入的 Idempotency-Key 非 UUID（已就地纠正，请修调用点）', provided);
      }
      const key = provided && isUuid(provided) ? provided : newUuid();
      config.idempotencyKey = key;
      config.headers.set('Idempotency-Key', key);
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => {
      const body = response.data as ApiResponse<unknown> | undefined;
      // 非统一封装（文件流、后端裸 404 页）直接透传；两枚码键一枚都不认 ⇒ 不算信封
      if (!body || typeof body !== 'object' || (!('code' in body) && !('resultCode' in body))) {
        if (response.status >= Http.BAD_REQUEST) {
          throw new ApiError('服务暂时不可用，请稍后重试', response.status, response.status);
        }
        recordDrift('响应缺少 {code,message,data,traceId} 封装', { status: response.status });
        return response;
      }
      // 实测：部分 §6.2 端点连 `traceId` 键都不写（不只是 null），一律记漂移
      if (body.traceId === null || body.traceId === undefined || body.traceId === '') {
        recordDrift('traceId 未透传（§6.0-③ 要求全链路，前端无法据此报障）', { present: 'traceId' in body });
      }
      if (!isEnvelopeSuccess(body)) {
        const apiError = ApiError.fromEnvelope(body, response.status, '请求失败');
        surfaceError(apiError, response.config);
        throw apiError;
      }
      response.data = stringifyWireIds(body.data);
      return response;
    },
    async (error: AxiosError<ApiResponse<unknown>>) => {
      const config = error.config as (InternalAxiosRequestConfig & { __retryCount?: number }) | undefined;
      const status = error.response?.status;
      const body = error.response?.data;

      // §6.0-④：429 与网络错误/5xx 走退避重试——S-13 落地前**只对只读方法生效**，见 shouldRetry
      const retriable = !error.response || status === Http.TOO_MANY || (status ?? 0) >= 500;
      if (config && retriable && shouldRetry(config)) {
        config.__retryCount = (config.__retryCount ?? 0) + 1;
        await backoff(config.__retryCount - 1);
        return client.request(config);
      }

      // 非 2xx 也可能带信封（422/409/423…）：同样只从 resultCode 取定因，`code` 留作显示
      const apiError = ApiError.fromEnvelope(body, status, error.message || '网络异常，请稍后重试');

      surfaceError(apiError, config);
      return Promise.reject(apiError);
    },
  );

  return client;
}

/** §6.0-⑤：分页 size 默认 20、上限 200 */
export const PAGE_SIZE_MAX = 200;
export const PAGE_SIZE_DEFAULT = 20;

export function clampPageSize(size?: number): number {
  const n = Math.floor(Number(size ?? PAGE_SIZE_DEFAULT));
  if (!Number.isFinite(n) || n < 1) return PAGE_SIZE_DEFAULT;
  return Math.min(n, PAGE_SIZE_MAX);
}

/** §6.0-⑤ 排序子句：`{ field, dir }`，多组按数组顺序传递 */
export interface SortClause {
  field: string;
  dir: 'asc' | 'desc';
}

/**
 * §6.0-⑤：排序参数 `sort=field,asc|desc`，**可多组、逗号分隔**（v1.6 已冻结分隔符），
 * 即 `sort=createdAt,desc,orderNo,asc`。不得使用重复 sort 参数，也不得自创其它分隔符。
 */
export function buildSort(clauses: SortClause | SortClause[]): string | undefined {
  const list = (Array.isArray(clauses) ? clauses : [clauses]).filter((item) => item?.field);
  if (list.length === 0) return undefined;
  return list.map((item) => `${item.field},${item.dir === 'asc' ? 'asc' : 'desc'}`).join(',');
}

/** 统一取数：返回 ApiResponse.data（响应拦截器已解封装） */
export async function unwrap<T>(promise: Promise<{ data: T }>): Promise<T> {
  const res = await promise;
  return res.data;
}
